import React, { useEffect, useState, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Invoice, LineItem } from '../../types';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/calculations';
interface QuoteApprovalModalProps {
  invoice: Invoice;
  lineItems: LineItem[];
  isOpen: boolean;
  onClose: () => void;
  onApproved: () => void;
}
export function QuoteApprovalModal({
  invoice,
  lineItems,
  isOpen,
  onClose,
  onApproved
}: QuoteApprovalModalProps) {
  const {
    getCustomerById,
    getTermsByCompanyId,
    createSignedApproval,
    convertQuoteToInvoice
  } = useData();
  const customer = getCustomerById(invoice.customer_id);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [customerName, setCustomerName] = useState(customer?.name || '');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Get company's default terms automatically
  const companyTerms = getTermsByCompanyId(invoice.company_id);
  const defaultTerms = companyTerms.find(t => t.is_default) || companyTerms[0];
  // Initialize canvas when modal opens
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Set canvas dimensions for high DPI
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * 2;
        canvas.height = rect.height * 2;
        ctx.scale(2, 2);
        // Clear and set white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Set drawing style
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [isOpen]);
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    setHasSignature(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = ('touches' in e ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = ('touches' in e ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };
  const handleApprove = async () => {
    if (!hasSignature || !customerName.trim() || !termsAccepted) {
      alert('Please provide your name, agree to the terms, and sign');
      return;
    }
    if (!defaultTerms) {
      alert('No terms and conditions available');
      return;
    }
    setIsSubmitting(true);
    try {
      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Canvas not found');
      const signatureData = canvas.toDataURL('image/png');
      await createSignedApproval({
        invoice_id: invoice.id,
        terms_id: defaultTerms.id,
        customer_name: customerName,
        signature_data: signatureData,
        signed_at: new Date(),
        ip_address: window.location.hostname
      });
      await convertQuoteToInvoice(invoice.id);
      onApproved();
      onClose();
    } catch (error) {
      console.error('Failed to approve quote:', error);
      alert('Failed to approve quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!defaultTerms) {
    return <Modal isOpen={isOpen} onClose={onClose} title="Approve Quote" data-id="element-196">
        <div className="text-center py-8" data-id="element-197">
          <p className="text-gray-600" data-id="element-198">
            No terms and conditions available for this company.
          </p>
          <Button onClick={onClose} className="mt-4" data-id="element-199">
            Close
          </Button>
        </div>
      </Modal>;
  }
  return <Modal isOpen={isOpen} onClose={onClose} title="Approve Quote" size="large" data-id="element-200">
      <div className="space-y-6" data-id="element-201">
        {/* Quote Summary */}
        <div className="bg-gray-50 rounded-lg p-4" data-id="element-202">
          <h3 className="font-semibold text-gray-900 mb-3" data-id="element-203">Quote Summary</h3>
          <div className="space-y-2" data-id="element-204">
            {lineItems.map(item => <div key={item.id} className="flex justify-between text-sm" data-id="element-205">
                <span className="text-gray-600" data-id="element-206">
                  {item.description} (×{item.quantity})
                </span>
                <span className="font-medium" data-id="element-207">
                  {formatCurrency(item.quantity * item.price)}
                </span>
              </div>)}
            <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-208">
              <div className="flex justify-between text-sm" data-id="element-209">
                <span className="text-gray-600" data-id="element-210">Subtotal</span>
                <span className="font-medium" data-id="element-211">
                  {formatCurrency(invoice.subtotal)}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-212">
                <span className="text-gray-600" data-id="element-213">
                  Tax ({invoice.tax_rate * 100}%)
                </span>
                <span className="font-medium" data-id="element-214">
                  {formatCurrency(invoice.tax_amount)}
                </span>
              </div>
              <div className="flex justify-between font-bold text-lg mt-2" data-id="element-215">
                <span data-id="element-216">Total</span>
                <span data-id="element-217">{formatCurrency(invoice.total_amount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div data-id="element-218">
          <h3 className="font-semibold text-gray-900 mb-2" data-id="element-219">
            Terms and Conditions: {defaultTerms.name}
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto bg-white prose prose-sm max-w-none" dangerouslySetInnerHTML={{
          __html: defaultTerms.content
        }} data-id="element-220" />
        </div>

        {/* Terms Agreement Checkbox */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-221">
          <input type="checkbox" id="terms-agreement" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-222" />
          <label htmlFor="terms-agreement" className="text-sm text-gray-700 cursor-pointer" data-id="element-223">
            I have read and agree to the terms and conditions above. By signing
            below, I approve this quote and understand it will be converted into
            an invoice that requires payment.
          </label>
        </div>

        {/* Customer Name */}
        <div data-id="element-224">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-225">
            Your Full Name *
          </label>
          <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter your full name" required data-id="element-226" />
        </div>

        {/* Signature Pad */}
        <div data-id="element-227">
          <div className="flex items-center justify-between mb-2" data-id="element-228">
            <label className="block text-sm font-medium text-gray-700" data-id="element-229">
              Your Signature *
            </label>
            <button type="button" onClick={clearSignature} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-230">
              Clear
            </button>
          </div>
          <canvas ref={canvasRef} className="w-full h-48 border-2 border-gray-300 rounded-lg cursor-crosshair bg-white touch-none" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} style={{
          touchAction: 'none'
        }} data-id="element-231" />
          <p className="text-xs text-gray-500 mt-1" data-id="element-232">
            Sign above using your mouse or touchscreen
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t" data-id="element-233">
          <Button onClick={onClose} variant="secondary" disabled={isSubmitting} className="flex-1" data-id="element-234">
            Cancel
          </Button>
          <Button onClick={handleApprove} disabled={!hasSignature || !customerName.trim() || !termsAccepted || isSubmitting} className="flex-1" data-id="element-235">
            {isSubmitting ? 'Approving...' : 'Approve Quote'}
          </Button>
        </div>
      </div>
    </Modal>;
}